import { DocumentModel } from "../types/document";
import { parseArgs } from "./parseArgs";
import { ToolResult } from "./toolExecutor";

export interface EditInfo {
  segmentId: string;
  find: string;
  replace: string;
  bookmarkName?: string;
  status: "pending" | "accepted" | "rejected";
}

/**
 * Given a completed tool call and its result, builds one or more EditInfo
 * objects for the UI edit-card list. Returns null if no edit card should be
 * shown (e.g. the call failed or is not an editing tool).
 */
export function buildEditInfo(
  toolName: string,
  rawArgs: object | string,
  result: ToolResult,
  docModel: DocumentModel
): EditInfo | EditInfo[] | null {
  if (!result.success) return null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const args = parseArgs<Record<string, any>>(rawArgs);

  switch (toolName) {
    case "search_and_replace": {
      if (!args.segmentId || !args.find || args.replace === undefined) return null;
      return {
        segmentId: args.segmentId,
        find: args.find,
        replace: args.replace,
        bookmarkName: result.bookmarkName,
        status: "pending",
      };
    }

    case "insert_paragraph": {
      if (!args.segmentId || !args.text) return null;
      return {
        segmentId: args.segmentId,
        find: `[insert ${args.position ?? "after"}]`,
        replace: args.text.length > 80 ? args.text.substring(0, 77) + "..." : args.text,
        bookmarkName: result.bookmarkName,
        status: "pending",
      };
    }

    case "delete_paragraph": {
      if (!args.segmentId) return null;
      const segment = docModel.segments.find((s) => s.segmentId === args.segmentId);
      const deletedText = segment
        ? segment.text.length > 80
          ? segment.text.substring(0, 77) + "..."
          : segment.text
        : args.segmentId;
      return {
        segmentId: args.segmentId,
        find: deletedText,
        replace: "[deleted]",
        bookmarkName: result.bookmarkName,
        status: "pending",
      };
    }

    case "search_and_replace_in_table": {
      if (!args.find || args.replace === undefined) return null;
      return {
        segmentId: `table${args.tableIndex}[${args.row},${args.column}]`,
        find: args.find,
        replace: args.replace,
        bookmarkName: result.bookmarkName,
        status: "pending",
      };
    }

    case "add_table_rows": {
      return {
        segmentId: `table${args.tableIndex ?? "?"}`,
        find: "[add rows]",
        replace: `${args.count ?? "?"} row(s) at ${args.position ?? "end"}`,
        bookmarkName: result.bookmarkName,
        status: "pending",
      };
    }

    case "add_table_columns": {
      return {
        segmentId: `table${args.tableIndex ?? "?"}`,
        find: "[add columns]",
        replace: `${args.count ?? "?"} column(s) at ${args.position ?? "end"}`,
        bookmarkName: result.bookmarkName,
        status: "pending",
      };
    }

    case "delete_table_rows": {
      return {
        segmentId: `table${args.tableIndex ?? "?"}`,
        find: `${args.count ?? "?"} row(s) at index ${args.rowIndex ?? "?"}`,
        replace: "[deleted]",
        bookmarkName: result.bookmarkName,
        status: "pending",
      };
    }

    case "delete_table_columns": {
      return {
        segmentId: `table${args.tableIndex ?? "?"}`,
        find: `${args.count ?? "?"} column(s) at index ${args.columnIndex ?? "?"}`,
        replace: "[deleted]",
        bookmarkName: result.bookmarkName,
        status: "pending",
      };
    }

    case "insert_table": {
      return {
        segmentId: args.segmentId ?? "?",
        find: `[insert table ${args.position ?? "after"}]`,
        replace: `${args.rows ?? "?"}×${args.columns ?? "?"} table`,
        bookmarkName: result.bookmarkName,
        status: "pending",
      };
    }

    case "insert_chained_paragraphs": {
      if (!args.segmentId || !Array.isArray(args.paragraphs) || args.paragraphs.length === 0)
        return null;
      const bookmarkNames: (string | undefined)[] = result.bookmarkNames ?? [];
      return args.paragraphs.map(
        (para: { text: string; style?: string }, i: number): EditInfo => ({
          segmentId: args.segmentId,
          find: `[insert ${args.position ?? "after"}]`,
          replace: para.text.length > 80 ? para.text.substring(0, 77) + "..." : para.text,
          bookmarkName: bookmarkNames[i],
          status: "pending",
        })
      );
    }

    default:
      return null;
  }
}
